import { ApolloClient, NormalizedCacheObject, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { COLLECTION_ITEM_PAGE_SIZE } from '../../../../../constants'
import { SEARCH } from '../../../../../lib/graphql/queries/SEARCH'
import { QueryStatus } from '../../../../../lib/hooks/types'
import { SearchResults } from '../../../../../types/graphql'

export type UseSearchPageOpts = {
  query: string
  selectedFilters: { key: string; values: string[] }[]
}

export const primeClientForUseSearchPageQuery = ({
  client,
  query,
}: {
  client: ApolloClient<NormalizedCacheObject>
  query: string
}) => {
  // @TODO: remove `any` generics for full type-safety
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return client.query<any, any>({
    query: SEARCH,
    variables: { query, first: COLLECTION_ITEM_PAGE_SIZE, selectedFilters: [] },
  })
}

export const useSearchPageQuery = ({ query, selectedFilters }: UseSearchPageOpts) => {
  const {
    data,
    fetchMore: fetchMoreSearchResults,
    previousData,
    loading,
    error,
    called,
  } = useQuery<{
    search: SearchResults
  }>(SEARCH, {
    variables: { query, selectedFilters, first: COLLECTION_ITEM_PAGE_SIZE },
    notifyOnNetworkStatusChange: true,
  })

  const searchData = (data ?? previousData)?.search

  const status = useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    if (!called && !searchData) return 'idle'
    return 'resolved'
  }, [called, error, loading, searchData])

  const searchFilters = (searchData?.items.filters ?? []).filter(
    filter => filter.filterValues && filter.filterValues.length > 1
  )
  const searchNumItems = searchData?.items.numItems ?? 0
  const searchPageInfo = searchData?.items.pageInfo
  const searchProducts = searchData?.items.nodes.map(node => node.product) ?? []
  const searchSuggestions = searchData?.suggestions.map(suggestion => suggestion.query) ?? []
  const colors = searchData?.items.colors ?? []

  return {
    colors,
    fetchMoreSearchResults,
    searchFilters,
    searchNumItems,
    searchPageInfo,
    searchProducts,
    searchSuggestions,
    status,
  }
}
